/**
 * WizardStepSigners - Passo 4: Definir signatários do contrato
 * 
 * RELEASE: v1.2.0 - 2026-02-19
 * - v1.1.0: Adicionado seletor de código de país + máscara no campo telefone
 *           para garantir formato E.164 exigido pela Autentique (+5562993252289)
 * - v1.2.0: Lista de papéis unificada com SIGNER_ROLES de @/types/contracts
 *           (elimina duplicação e adiciona papel "Vendedor" que estava ausente)
 */
import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Plus, Trash2, Mail, MessageCircle } from 'lucide-react';
import type { WizardSigner } from './ContractWizardDialog';
import { countryCodes, extractCountryCode } from '@/lib/country-codes';
import { applyPhoneMask } from '@/lib/input-masks';
// Importa lista centralizada de papéis (inclui Vendedor, Representante Legal com chave correta)
import { SIGNER_ROLES } from '@/types/contracts';

interface WizardStepSignersProps {
  signers: WizardSigner[];
  onChangeSigners: (signers: WizardSigner[]) => void;
  /** Métodos de entrega permitidos pelo template selecionado */
  allowedDeliveryMethods?: ('email' | 'whatsapp')[];
}

export function WizardStepSigners({ signers, onChangeSigners, allowedDeliveryMethods = ['email', 'whatsapp'] }: WizardStepSignersProps) {
  /**
   * Estado local para armazenar código de país por signatário
   * Chave: índice do signatário, Valor: código de país (ex: '+55')
   */
  const [phoneCodes, setPhoneCodes] = React.useState<Record<number, string>>(() => {
    const initial: Record<number, string> = {};
    signers.forEach((s, i) => {
      const { countryCode } = extractCountryCode(s.signer_phone);
      initial[i] = countryCode;
    });
    return initial;
  });

  /** Atualiza um campo de um signatário */
  const handleChange = (index: number, field: keyof WizardSigner, value: string | number) => {
    const updated = [...signers];
    updated[index] = { ...updated[index], [field]: value };
    onChangeSigners(updated);
  };

  /** Adiciona novo signatário com delivery_method padrão baseado nos métodos permitidos */
  const addSigner = () => {
    const defaultMethod = allowedDeliveryMethods[0] || 'email';
    onChangeSigners([
      ...signers,
      {
        signer_name: '',
        signer_email: '',
        signer_phone: '',
        signer_role: 'contratado',
        sign_order: signers.length + 1,
        delivery_method: defaultMethod,
      },
    ]);
  };

  /** Indica se o toggle de método deve ser exibido (mais de 1 método permitido) */
  const showDeliveryToggle = allowedDeliveryMethods.length > 1;

  /** Remove signatário */
  const removeSigner = (index: number) => {
    if (signers.length <= 1) return;
    const updated = signers.filter((_, i) => i !== index).map((s, i) => ({
      ...s,
      sign_order: i + 1,
    }));
    onChangeSigners(updated);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Defina quem precisa assinar este contrato. Pelo menos um signatário é obrigatório.
      </p>

      <div className="space-y-4">
        {signers.map((signer, index) => (
          <div key={index} className="p-3 rounded-lg border space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">
                Signatário {index + 1}
              </span>
            {/* Oculta lixeira para signatários pré-configurados no contrato publicado (obrigatórios) */}
            {signers.length > 1 && !signer.isPreConfigured && (
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeSigner(index)}>
                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                </Button>
              )}
            </div>

            {/* Seletor de método de entrega - exibe apenas se mais de 1 método permitido */}
            {showDeliveryToggle ? (
              <div className="grid gap-1">
                <Label className="text-xs">Enviar link via</Label>
                <ToggleGroup
                  type="single"
                  value={signer.delivery_method}
                  onValueChange={(v) => { if (v) handleChange(index, 'delivery_method', v); }}
                  className="justify-start"
                >
                  {allowedDeliveryMethods.includes('email') && (
                    <ToggleGroupItem value="email" size="sm" className="text-xs gap-1 h-7 px-2.5">
                      <Mail className="h-3 w-3" /> E-mail
                    </ToggleGroupItem>
                  )}
                  {allowedDeliveryMethods.includes('whatsapp') && (
                    <ToggleGroupItem value="whatsapp" size="sm" className="text-xs gap-1 h-7 px-2.5">
                      <MessageCircle className="h-3 w-3" /> WhatsApp
                    </ToggleGroupItem>
                  )}
                </ToggleGroup>
              </div>
            ) : (
              <div className="grid gap-1">
                <Label className="text-xs">Enviar link via</Label>
                <div className="flex items-center gap-1.5 h-7 text-xs text-muted-foreground">
                  {allowedDeliveryMethods[0] === 'whatsapp' ? (
                    <><MessageCircle className="h-3 w-3" /> WhatsApp</>
                  ) : (
                    <><Mail className="h-3 w-3" /> E-mail</>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-x-3 gap-y-3">
              <div className="grid gap-1">
                <Label className="text-xs">Nome *</Label>
                <Input
                  value={signer.signer_name}
                  onChange={(e) => handleChange(index, 'signer_name', e.target.value)}
                  placeholder="Nome completo"
                  className="h-9 text-sm"
                  disabled={signer.hasDefaultData && !!signer.signer_name}
                />
              </div>
              <div className="grid gap-1">
                <Label className="text-xs">Papel</Label>
                <Select
                  value={signer.signer_role}
                  onValueChange={(v) => handleChange(index, 'signer_role', v)}
                  disabled={signer.hasDefaultData}
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SIGNER_ROLES.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1">
                <Label className="text-xs">
                  E-mail {signer.delivery_method === 'email' && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  value={signer.signer_email}
                  onChange={(e) => handleChange(index, 'signer_email', e.target.value)}
                  placeholder="email@exemplo.com"
                  type="email"
                  className="h-9 text-sm"
                  disabled={signer.hasDefaultData && !!signer.signer_email}
                />
                {signer.delivery_method === 'email' && !signer.signer_email && (
                  <span className="text-[10px] text-destructive">Obrigatório para envio por e-mail</span>
                )}
              </div>
              <div className="grid gap-1">
                <Label className="text-xs">
                  Telefone {signer.delivery_method === 'whatsapp' && <span className="text-destructive">*</span>}
                </Label>
                <div className="flex gap-1.5">
                  {/* Seletor de código de país */}
                  <Select
                    value={phoneCodes[index] || '+55'}
                    onValueChange={(code) => {
                      // Atualiza código de país local
                      setPhoneCodes(prev => ({ ...prev, [index]: code }));
                      // Recompõe valor E.164: código + dígitos locais
                      const currentLocal = extractCountryCode(signer.signer_phone).localNumber;
                      handleChange(index, 'signer_phone', currentLocal ? `${code}${currentLocal}` : '');
                    }}
                    disabled={signer.hasDefaultData && !!signer.signer_phone}
                  >
                    <SelectTrigger className="h-9 text-xs w-[100px] shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {countryCodes.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          <span className="flex items-center gap-1">
                            <span>{c.flag}</span>
                            <span>{c.code}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Input com máscara visual - valor E.164 propagado para signer_phone */}
                  <Input
                    value={applyPhoneMask(extractCountryCode(signer.signer_phone).localNumber)}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '');
                      const code = phoneCodes[index] || '+55';
                      // Propaga formato E.164 limpo: +5562993252289
                      handleChange(index, 'signer_phone', digits ? `${code}${digits}` : '');
                    }}
                    placeholder="(11) 99999-9999"
                    className="h-9 text-sm flex-1"
                    disabled={signer.hasDefaultData && !!signer.signer_phone}
                  />
                </div>
                {signer.delivery_method === 'whatsapp' && (() => {
                  const localDigits = extractCountryCode(signer.signer_phone).localNumber;
                  if (!localDigits || localDigits.length < 10) {
                    return <span className="text-[10px] text-destructive">Mínimo 10 dígitos (DDD + número) para WhatsApp</span>;
                  }
                  return null;
                })()}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Button variant="outline" size="sm" onClick={addSigner} className="w-full text-xs gap-1.5">
        <Plus className="h-3.5 w-3.5" />
        Adicionar signatário
      </Button>
    </div>
  );
}
